import { components } from '@doltech/dto';
import {
  AvailableForEnum,
  DictationSentenceStatusEnum,
  DictationWordStatusEnum,
  DictationWordTypeEnum,
  LevelEnum,
  ResourceStatusEnum,
} from '@doltech/utils/lib/constants';
import { convertCurlyQuotationMark } from '@doltech/utils/lib/text';
import { getSnapshot, Instance, types } from 'mobx-state-tree';
import { nanoid } from 'nanoid';

import { FileInfo, ImageInfo } from '../../question-data-model/Script/abtract-script.store';
import { DictationStore } from './dictation.store';
import { DoDictationSentence, DoDictationSentenceInstance } from './do-dictation-sentence.store';
import { DoDictationWord } from './do-dictation-word.store';
import { DUAL_TOGGLE } from '@doltech/domains/dictation/shared/constant';

export const DoDictationStore = DictationStore.named('DoDictationStore')
  .props({
    id: types.maybeNull(types.string),
    dictationId: types.maybeNull(types.string),
    name: types.maybeNull(types.string),
    type: types.maybeNull(types.string),
    cover: types.maybeNull(ImageInfo),
    sourceFile: types.maybeNull(FileInfo),
    subtitle: types.maybeNull(FileInfo),
    noOfSentences: types.maybeNull(types.number),
    noOfWords: types.maybeNull(types.number),
    topicId: types.maybeNull(types.string),
    topicName: types.maybeNull(types.string),
    level: types.maybeNull(types.enumeration<LevelEnum>('LevelEnum', Object.values(LevelEnum))),
    lengthInSeconds: types.maybeNull(types.number),
    status: types.maybeNull(
      types.enumeration<ResourceStatusEnum>('ResourceStatusEnum', Object.values(ResourceStatusEnum))
    ),
    availableFor: types.maybeNull(
      types.enumeration<AvailableForEnum>('AvailableForEnum', Object.values(AvailableForEnum))
    ),
    sentences: types.array(DoDictationSentence),
    /**
     * Sum of total times that user do this dictation.
     * Ex: User do this dictation 3 times, this variable hold
     * total time user has done
     */
    totalTimeTakenInSeconds: types.optional(types.number, 0),
    /**
     * For current UserDictation scope
     */
    timeTakenInSeconds: types.optional(types.number, 0),
    initialized: types.optional(types.boolean, false),
    fetchedUserDictation: types.optional(types.boolean, false),
    noOfUsersCompleted: types.optional(types.number, 0),
    mobileDrawerVisible: types.optional(types.boolean, false),
    audioVolume: types.optional(types.number, 1),
    playbackRate: types.optional(types.number, 1),
  })
  .volatile(() => ({
    isLearnedAllShadowingPronun: false,
    isLearnedAllPopularWordPronun: false,
  }))
  .actions((self) => {
    return {
      init(
        dictation: components['schemas']['DictationDTO'],
        userAnswers?: components['schemas']['DictationAnswers'],
        result?: any
      ) {
        const {
          id,
          name,
          cover,
          noOfSentences,
          level,
          lengthInSeconds,
          topic,
          sentences,
          sourceFile,
          type,
        } = dictation;
        self.initialized = true;
        self.playbackRate = 1;
        self.currentSentenceIndex = 0;
        self.id = id;
        self.name = name;
        self.type = type;
        self.sourceFile = FileInfo.create(sourceFile);
        self.cover = ImageInfo.create(cover);
        self.noOfSentences = noOfSentences;
        self.level = level as LevelEnum;
        self.lengthInSeconds = lengthInSeconds;
        self.topicId = topic?.id;
        self.topicName = topic?.name;

        self.sentences.replace(
          (sentences || []).map((sentence, sentenceIndex) => {
            const answeredSentence = userAnswers?.sentences?.[sentenceIndex] as any;

            const doDictationSentence = DoDictationSentence.create({
              ...sentence,
              key: sentence.key,
              status: DictationSentenceStatusEnum.UNTOUCHED,
              isLearnedShadowingPronun: answeredSentence?.isLearnedShadowingPronun,
              popularWords: answeredSentence?.popularWords || (sentence as any)?.popularWords,
              learnedHistory: answeredSentence?.learnedHistory,
              words: sentence.words.map((word, wordIndex) => {
                const answeredWord = answeredSentence?.words?.[wordIndex];

                const doDictationWord = DoDictationWord.create({
                  id: nanoid(),
                  status: DictationWordStatusEnum.UNTOUCHED,
                  key: word.key,
                  value: convertCurlyQuotationMark(word.value),
                  type: word.type as DictationWordTypeEnum,
                  studentValue: answeredWord?.value,
                });

                doDictationWord.check();

                return doDictationWord;
              }),
              dualToggle: DUAL_TOGGLE.FREE_TEXT,
              freeTextValue: answeredSentence?.freeTextValue,
            });

            if (answeredSentence) {
              try {
                doDictationSentence.freeTextCheck().then(() => {
                  if (doDictationSentence.status !== DictationSentenceStatusEnum.CORRECT) {
                    doDictationSentence.check().then(() => {
                      if (doDictationSentence.isWordsCorrect) {
                        doDictationSentence.setChecked(true);
                        doDictationSentence.setCorrect();
                      }
                    });
                  }
                });
              } catch (err) {
                console.log(err);
              }
            }

            return doDictationSentence;
          })
        );
        self.isLearnedAllShadowingPronun = result?.isLearnedAllShadowingPronun;
        self.isLearnedAllPopularWordPronun = result?.isLearnedAllPopularWordPronun;
      },
      updateAllDualToggle(toggleChange) {
        if (self.sentences.length) {
          self.sentences.forEach((eachSentence) => {
            eachSentence.dualToggle = toggleChange;
          });
        }
      },
      next() {
        if (self.currentSentenceIndex < self.sentences.length - 1) {
          self.currentSentenceIndex += 1;
        }
      },
      prev() {
        if (self.currentSentenceIndex > 0) {
          self.currentSentenceIndex -= 1;
        }
      },
      jumpToSentenceByIndex(index: number) {
        self.currentSentenceIndex = index;
      },
      preparePayload(): components['schemas']['UserSubmitDictationDTO'] {
        const sentences = getSnapshot(self.sentences) as any[];
        const sentenceInstances = self.sentences as DoDictationSentenceInstance[];
        return {
          noOfCompletedSentences: sentenceInstances.reduce((sum, sentence) => {
            return sum + (sentence.isCorrect ? 1 : 0);
          }, 0),
          userAnswers: {
            sentences: sentences.map((sentence) => {
              return {
                ...sentence,
                words: sentence.words.map((word) => ({
                  key: word.key,
                  type: word.type,
                  value: word.studentValue,
                })),
              };
            }),
          },
          timeTakenInSeconds: self.timeTakenInSeconds,
        };
      },
      updatePlaybackSpeed(speed) {
        self.playbackRate = speed;
      },
      setFetchedUserDictation(val = true) {
        self.fetchedUserDictation = val;
      },
      setTotalTimeTakenInSeconds(val: number) {
        self.totalTimeTakenInSeconds = val;
      },
      setTimeTakenInSeconds(val: number) {
        self.timeTakenInSeconds = val;
      },
      increaseTimeTakenInSeconds(val: number) {
        self.timeTakenInSeconds += val;
      },
      setNoOfUsersCompleted(val: number) {
        self.noOfUsersCompleted = val;
      },
      setMobileDrawerVisible(val: boolean) {
        self.mobileDrawerVisible = val;
      },
      setAudioVolume(val: number) {
        self.audioVolume = val;
      },
      setLearnedAllShadowingPronun(isLearnedAllShadowingPronun: boolean) {
        self.isLearnedAllShadowingPronun = isLearnedAllShadowingPronun;
      },
      setLearnedAllPopularWordPronun(isLearnedAllPopularWordPronun: boolean) {
        self.isLearnedAllPopularWordPronun = isLearnedAllPopularWordPronun;
      },
      checkAnswer() {}, // This method to trigger action
      checkAnswerWithLoginRequire() {}, // This method to trigger action
    };
  })
  .views((self) => {
    return {
      get currentSentence(): DoDictationSentenceInstance {
        const sentences = self.sentences as DoDictationSentenceInstance[];
        return sentences.find((_, idx) => idx === self.currentSentenceIndex);
      },
      get canPrev() {
        return self.currentSentenceIndex > 0;
      },
      get canNext() {
        return self.currentSentenceIndex < self.sentences.length - 1;
      },
      get noOfCompletedSentences() {
        const sentenceInstances = self.sentences as DoDictationSentenceInstance[];
        return sentenceInstances.reduce((sum, sentence) => {
          return sum + (sentence.isCorrect ? 1 : 0);
        }, 0);
      },
    };
  });

export interface DoDictationStoreInstance extends Instance<typeof DoDictationStore> {}
